// src/components/business/business/BusinessForm.tsx
import React, {
  ChangeEvent,
  FC,
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Grid,
  Typography,
  Button,
  FormControl,
  Box,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  SelectChangeEvent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import jwtDecode from "jwt-decode";

import BusinessAddressForm, { BusinessAddress } from "./businessAddressForm";
import { UserContext } from "../../auth/auth-context";
import { hasPermission, Permissions } from "../../../util/accessControl";
import { rootClientPath } from "../../../util/link";
import {
  POST_BUSINESS_BASIC,
  UPDATE_BUSINESS_BASIC,
  SEND_EMAIL_MUTATION,
} from "../../../graphql/mutations";
import { UPDATE_DEFAULT_BUSINESS } from "../../../graphql/mutations";
import { GET_ALL_COUNTRIES } from "../../../graphql/queries/countryStateCity";
import { useSupportedCountries } from '../../../util/platformAccess';
import {
  BusinessQueryNames,
  GET_BILLING_SUBSCRIPTION,
  GET_BUSINESS_CATEGORIES,
  GET_PLANS,
  GET_TOKEN,
} from "../../../graphql/queries";
import { BusinessBasic, BusinessCategory } from "../../../types";
import { useSelectedBusiness } from "../../../redux/store";
import { useSelectBusinesses } from "../../../redux/store";
import {
  setSelectedBusiness,
} from "../../../redux/actions/businessActions";
import useLoadReduxStoreData, {
  normalizeBusinessData,
} from "../../../redux/reducers/loadReduxStoreData";
import { BusinessStatus } from "../../../util/utils";
import UpgradePlan from "../../../util/upgrade/upgradePlan";
import ConfirmModal from "./businessSubmitConfirmation";
import { useAuthStatus } from "../../../util/useAuthStatus";
import { FormProvider, useForm } from "react-hook-form";
import BusinessSettingsPage from "../../../../core/business/forms/BusinessSettingsPage";
import { resolveProductId } from "../../../../core/business/product";
import {
  filterBusinessCategoriesForProduct,
  getDisplayedBusinessSubcategories,
  getDisplayedBusinessTopLevelCategories,
  getBusinessSubcategory,
  getHealthcareTopLevelBusinessCategory,
  getTopLevelBusinessCategory,
  isHealthcareBusinessCategory,
  isHealthcareCategoryName,
} from "../../../../core/business/category";
import {
  hasCareWorkspaceAccess,
  getProductPlanTierCode,
} from "../../../../core/business/productAccess";
import {
  getDirectoryToggleVisibility,
  shouldRequireBusinessVisibilityUpgrade,
} from "../../../../core/business/directoryVisibilityAccess";
import ContactLinksAccordion from "./contactLinksAccordion";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { hasValidBusinessSlugCharacters } from "../../../util/businessSlug";
import { setStoredVertical } from "../../../routes/verticalPref";
import { getRuntimeAppProfile } from "../../../runtime/appProfile";

export const GET_ROLES = gql`
  query GetRoles {
    roles {
      id
      name
      permissions
    }
  }
`;

interface IBusinessFormProps {
  isDisabled?: boolean;
  onSubmitSuccess?: () => void;
  onCancel?: () => void;
  isNewBusiness?: boolean;
  defaultValues?: any;
}

type DynamicFieldKey = "emails" | "links";

const hasText = (v?: string) => !!(v && v.trim().length > 0);
const normalizeLicense = (value?: string) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
const isAddressValid = (addr?: BusinessAddress) =>
  !!(
    addr &&
    Number(addr.countryId) > 0 &&
    Number(addr.stateId) > 0 &&
    Number(addr.cityId) > 0 &&
    hasText(addr.street) &&
    hasText(addr.zipcode)
  );

const socialMediaOptions = [
  "facebook",
  "twitter",
  "instagram",
  "linkedin",
  "youtube",
  "web",
  "other",
];

const tooltipData = {
  url: "This is your Timely365 business profile URL for sharing with others. Changing this URL will break any previously shared links—including staff links—so you'll need to update those to keep them working.",
  displayOnBookingPage:
    "Display this business on the Timely365 Booking Page for public visibility. This can only be enabled after required verification is complete.",
  businessProfileName:
    "If you have a registered business, use your official business name. Otherwise, enter your full legal name.",
};

const standardPlanRequired = 2;
const ACTIVE_BOOKING_STATUSES = new Set([
  "trialing",
  "active",
  "past_due",
  "canceling",
]);
const LOCAL_WORKSPACE_PRODUCTS_KEY = "planyLocalWorkspaceProducts";
const WORKSPACE_PRODUCTS_CHANGED_EVENT = "plany:workspace-products-change";
type DirectoryVisibilityKey = "care" | "booking";
const DIRECTORY_VISIBILITY_STORAGE_PREFIX = "business-directory-visibility";

const getDirectoryVisibilityStorageKey = (
  businessKey: string,
  directory: DirectoryVisibilityKey
) => `${DIRECTORY_VISIBILITY_STORAGE_PREFIX}:${businessKey}:${directory}`;

const readStoredDirectoryVisibility = (
  businessKey: string,
  directory: DirectoryVisibilityKey
) => {
  try {
    const raw = localStorage.getItem(
      getDirectoryVisibilityStorageKey(businessKey, directory)
    );
    if (raw == null) return null;
    return raw === "true";
  } catch (_error) {
    return null;
  }
};

const writeStoredDirectoryVisibility = (
  businessKey: string,
  directory: DirectoryVisibilityKey,
  value: boolean
) => {
  try {
    localStorage.setItem(
      getDirectoryVisibilityStorageKey(businessKey, directory),
      String(Boolean(value))
    );
  } catch (_error) {
    // ignore storage failures
  }
};

const initializeWorkspaceProductsForNewBusiness = (
  businessId: number,
  productId?: string | null
) => {
  if (!businessId || typeof window === "undefined") return;

  const normalizedProductId = productId === "care" ? "care" : "booking";
  const nextState = {
    care: normalizedProductId === "care",
    booking: normalizedProductId === "booking",
  };

  try {
    const raw = window.localStorage.getItem(LOCAL_WORKSPACE_PRODUCTS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const safeParsed = parsed && typeof parsed === "object" ? parsed : {};
    safeParsed[String(businessId)] = nextState;
    window.localStorage.setItem(
      LOCAL_WORKSPACE_PRODUCTS_KEY,
      JSON.stringify(safeParsed)
    );
    window.dispatchEvent(
      new CustomEvent(WORKSPACE_PRODUCTS_CHANGED_EVENT, {
        detail: { businessId, products: nextState },
      })
    );
  } catch (_error) {
    // ignore storage failures
  }
};

const buildFormWithDirectoryVisibility = ({
  baseForm,
  businessKey,
  productId,
}: {
  baseForm: BusinessBasic;
  businessKey: string;
  productId?: string | null;
}) => {
  const persistedVisibility = Boolean(baseForm.displayOnBookingPage);
  const displayOnCareDirectory =
    readStoredDirectoryVisibility(businessKey, "care") ?? persistedVisibility;
  const displayOnBookingDirectory =
    readStoredDirectoryVisibility(businessKey, "booking") ?? persistedVisibility;
  const currentWorkspaceVisibility =
    productId === "care"
      ? displayOnCareDirectory
      : productId === "booking"
        ? displayOnBookingDirectory
        : persistedVisibility;

  return {
    ...baseForm,
    displayOnCareDirectory,
    displayOnBookingDirectory,
    displayOnBookingPage: currentWorkspaceVisibility,
  } as BusinessBasic;
};

const BusinessForm: FC<IBusinessFormProps> = ({
  isDisabled: disabledProp = false,
  onSubmitSuccess,
  onCancel,
  isNewBusiness,
  defaultValues,
}) => {
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser!;
  const { updateProviderUser } = userContext as any;
  const selectedBusinessFromStore = useSelectedBusiness();
  const allBusinesses = useSelectBusinesses();
  const forceNewFromState = Boolean((location.state as any)?.forceNewBusiness);
  const explicitNewBusiness = Boolean(isNewBusiness || forceNewFromState);
  const selectedBusiness = useMemo(() => {
    if (explicitNewBusiness) {
      return null;
    }
    if (selectedBusinessFromStore?.businessBasic?.id) {
      return selectedBusinessFromStore;
    }
    const preferredId = Number(providerUser?.defaultBusinessId || 0);
    if (preferredId > 0) {
      const fallback = allBusinesses.find(
        (b: any) => Number(b?.businessBasic?.id) === preferredId
      );
      if (fallback) return fallback;
    }
    return selectedBusinessFromStore;
  }, [
    explicitNewBusiness,
    selectedBusinessFromStore,
    allBusinesses,
    providerUser?.defaultBusinessId,
  ]);
  const businessBasic = selectedBusiness?.businessBasic;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const methods = useForm({ defaultValues });
  const suggestedAfhLicense = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const stateLicense = (location.state as any)?.suggestedAfhLicense;
    const queryLicense = searchParams.get("afhLicense");
    return String(stateLicense || queryLicense || "");
  }, [location.search, location.state]);
  const suggestedCategoryId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const stateCategoryId = Number((location.state as any)?.prefillCategoryId || 0);
    const queryCategoryId = Number(searchParams.get("categoryId") || 0);
    return stateCategoryId || queryCategoryId || 0;
  }, [location.search, location.state]);
  const suggestedSubcategoryId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const stateSubcategoryId = Number((location.state as any)?.prefillSubcategoryId || 0);
    const querySubcategoryId = Number(searchParams.get("subcategoryId") || 0);
    return stateSubcategoryId || querySubcategoryId || 0;
  }, [location.search, location.state]);
  const suggestedBusinessName = useMemo(() => {
    const stateBusinessName = (location.state as any)?.suggestedBusinessName;
    return String(stateBusinessName || "");
  }, [location.state]);
  const mvrFromQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("mvr");
  }, [location.search]);
  const manualVerificationRequested =
    Boolean((location.state as any)?.manualVerificationRequired) ||
    mvrFromQuery === "1";

  const [addrSubmitAttempted, setAddrSubmitAttempted] = useState(false);
  const [addrExpanded, setAddrExpanded] = useState(false);
  const addrHeaderRef = React.useRef<HTMLDivElement | null>(null);

  // --- GraphQL ---
  const { loading: loadingCountries, data: countryDataRaw } =
    useQuery(GET_ALL_COUNTRIES);
  const { countries: supportedCountries, loading: loadingSupportedCountries, error: supportedCountriesError } = useSupportedCountries();
  const { loading: loadingCats, data: categoryDataRaw } = useQuery(
    GET_BUSINESS_CATEGORIES
  );
  const activeBusinessId =
    Number(selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId || 0) ||
    0;
  const { data: bookingBillingData } = useQuery(GET_BILLING_SUBSCRIPTION, {
    variables: { businessId: activeBusinessId },
    skip: !activeBusinessId,
    fetchPolicy: "network-only",
  });
  const { data: plansData } = useQuery(GET_PLANS, {
    skip: !activeBusinessId,
  });

  const countries = (countryDataRaw?.getAllCountries || []).filter((country: any) =>
    supportedCountries.some((supported: any) => supported.code === country.code.toUpperCase()) ||
    (!explicitNewBusiness && Number(businessBasic?.id) > 0 && Number(country.id) === Number(businessBasic?.countryId))
  );
  const rawCategories: BusinessCategory[] =
    categoryDataRaw?.getBusinessCategories || [];
  const { isLoggedIn } = useAuthStatus();
  const { refetch: reloadBusinesses } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: isLoggedIn }
  );
  const [postBusiness] = useMutation(POST_BUSINESS_BASIC);
  const [updateBusinessM] = useMutation(UPDATE_BUSINESS_BASIC);
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);
  const [updateDefaultBusiness] = useMutation(UPDATE_DEFAULT_BUSINESS);
  // Determine if the user should start with an empty form
  const shouldInitNew =
    explicitNewBusiness ||
    (!!providerUser?.userId &&
      Number(providerUser?.defaultBusinessId || 0) <= 0 &&
      Number(selectedBusiness?.businessBasic?.id || 0) <= 0);

  // --- Helpers ---
  const resolveBusinessCategoryFields = React.useCallback(
    (
      sourceBusiness: any,
      fallbackBasic?: Partial<BusinessBasic> | null,
      fallbackCategoryId?: number | null,
      fallbackSubcategoryId?: number | null
    ) => {
      const sourceTopLevelId = Number(
        sourceBusiness?.businessTopCategory?.id ||
          sourceBusiness?.businessCategory?.id ||
          0
      );
      const sourceSubcategoryId = Number(
        sourceBusiness?.businessSubcategory?.id || 0
      );
      const basicTopLevelId = Number(fallbackBasic?.businessCategoryId || 0);
      const basicSubcategoryId = Number(
        (fallbackBasic as any)?.businessSubcategoryId || 0
      );
      const effectiveCategoryId =
        sourceSubcategoryId ||
        basicSubcategoryId ||
        sourceTopLevelId ||
        basicTopLevelId ||
        0;
      const effectiveCategory =
        rawCategories.find(
          (category) => Number(category.id) === Number(effectiveCategoryId)
        ) || null;
      const resolvedTopLevelId =
        sourceTopLevelId ||
        Number(getTopLevelBusinessCategory(effectiveCategory as any)?.id || 0) ||
        basicTopLevelId ||
        Number(fallbackCategoryId || 0);
      const resolvedSubcategoryId =
        sourceSubcategoryId ||
        basicSubcategoryId ||
        Number(getBusinessSubcategory(effectiveCategory as any)?.id || 0) ||
        Number(fallbackSubcategoryId || 0);

      return {
        businessCategoryId: resolvedTopLevelId || null,
        businessSubcategoryId: resolvedSubcategoryId || null,
      };
    },
    [rawCategories]
  );

  const syncBusinessContext = React.useCallback(
    async (targetBusinessId: number) => {
      if (!targetBusinessId) return;

      dispatch(setSelectedBusiness(targetBusinessId));

      const applyFallbackContext = () => {
        if (typeof updateProviderUser === "function") {
          updateProviderUser({ defaultBusinessId: targetBusinessId });
        }
      };

      if (Number(providerUser?.userId || 0) <= 0) {
        applyFallbackContext();
        return;
      }

      try {
        const refreshedTokenResult = await apolloClient.query({
          query: GET_TOKEN,
          variables: {
            userId: Number(providerUser.userId),
            businessId: targetBusinessId,
            staffId: providerUser?.staffId
              ? Number(providerUser.staffId)
              : null,
            subscriptionId: providerUser?.subscriptionId
              ? Number(providerUser.subscriptionId)
              : null,
          },
          fetchPolicy: "network-only",
        });
        const refreshedToken = refreshedTokenResult?.data?.getToken?.token;
        if (!refreshedToken) {
          applyFallbackContext();
          return;
        }

        localStorage.setItem("token", refreshedToken);
        const decodedToken: any = jwtDecode(refreshedToken);
        updateProviderUser(decodedToken);

        const decodedDefaultBusinessId = Number(
          decodedToken?.defaultBusinessId || targetBusinessId
        );
        if (decodedDefaultBusinessId > 0) {
          dispatch(setSelectedBusiness(decodedDefaultBusinessId));
        }
      } catch (contextError) {
        console.error("Unable to refresh business token after save:", contextError);
        applyFallbackContext();
      }
    },
    [
      apolloClient,
      dispatch,
      providerUser?.staffId,
      providerUser?.subscriptionId,
      providerUser?.userId,
      updateProviderUser,
    ]
  );

  const getInitialForm = (): BusinessBasic => ({
    id: 0,
    claimStatus: "",
    businessCategoryId: suggestedCategoryId || null,
    businessSubcategoryId: suggestedSubcategoryId || null,
    name: suggestedBusinessName || "",
    description: "",
    currency: "USD",
    status: BusinessStatus.ACTIVE,
    street: "",
    address: "",
    countryId: undefined,
    stateId: undefined,
    cityId: undefined,
    zipcode: "",
    cityName: "",
    stateName: "",
    stateCode: "",
    lat: 0,
    lng: 0,
    hasLocation: true,
    url: "",
    emails: [""],
    phoneNumbers: [{ countryCode: "", phoneNumber: "" }],
    faxNumber: "",
    links: [],
    images: [],
    createdBy: providerUser.userId,
    stage: 0,
    displayOnBookingPage: false,
    displayOnCareDirectory: false,
    displayOnBookingDirectory: false,
  });

  // --- State Declarations ---
  const [form, setForm] = useState<BusinessBasic>(
    shouldInitNew
      ? getInitialForm()
      : buildFormWithDirectoryVisibility({
          baseForm: {
            ...(businessBasic || getInitialForm()),
            ...resolveBusinessCategoryFields(selectedBusiness, businessBasic),
          } as BusinessBasic,
          businessKey: String(
            businessBasic?.id || selectedBusiness?.businessBasic?.id || "draft:initial"
          ),
        })
  );

  const [address, setAddress] = useState<BusinessAddress | undefined>(
    !shouldInitNew && businessBasic
      ? {
        street: businessBasic.street,
        countryId: Number(businessBasic.countryId),
        stateId: businessBasic.stateId,
        cityId: businessBasic.cityId,
        zipcode: businessBasic.zipcode,
      }
      : undefined
  );

  const addressValid = useMemo(() => isAddressValid(address), [address]);
  const showHeaderError =
    !addressValid && (addrSubmitAttempted || addrExpanded);

  const [touched, setTouched] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [socialList, setSocialList] = useState<string[]>(socialMediaOptions);
  const [isDirty, setIsDirty] = useState(false);
  const [newBizId, setNewBizId] = useState<any>();
  const [afhLicenseNumber, setAfhLicenseNumber] = useState(
    suggestedAfhLicense
  );
  const [afhLicenseError, setAfhLicenseError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const hasInitNewFormRef = React.useRef(false);
  const initializedNewDraftKeyRef = React.useRef<string | null>(null);
  const initializedBusinessIdRef = React.useRef<number | null>(null);
  const clearNewBusinessRoute = React.useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    ["new", "afhLicense", "categoryId", "subcategoryId", "mvr"].forEach((key) =>
      searchParams.delete(key)
    );
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString() ? `?${searchParams.toString()}` : "",
        hash: location.hash,
      },
      { replace: true, state: {} }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  const selectedCategory = useMemo(() => {
    const effectiveCategoryId =
      (form as any).businessSubcategoryId ?? form.businessCategoryId;
    return rawCategories.find((c) => Number(c.id) === Number(effectiveCategoryId));
  }, [rawCategories, form]);

  const runtimeVerticalId = "booking";

  const productId = "booking";
  const categories: BusinessCategory[] = useMemo(
    () =>
      filterBusinessCategoriesForProduct(
        rawCategories,
        productId
      ) as BusinessCategory[],
    [rawCategories, productId]
  );

  const isHealthcareCategorySelected = useMemo(() => {
    const selectedTopLevelCategory = getDisplayedBusinessTopLevelCategories(
      categories as any,
      productId
    ).find(
      (category: any) => Number(category.id) === Number(form.businessCategoryId)
    );
    return (
      isHealthcareCategoryName(selectedTopLevelCategory?.name) ||
      String((selectedTopLevelCategory as any)?.slug || "").toLowerCase() ===
        "healthcare"
    );
  }, [categories, productId, form.businessCategoryId]);
  const isAFHSelected = false;
  const hasCareAccess = false;
  const visibilityAccessUser = useMemo(
    () => ({
      ...providerUser,
      exemptedPlanId:
        businessBasic?.exemptedPlanId ??
        selectedBusiness?.businessBasic?.exemptedPlanId ??
        providerUser?.exemptedPlanId,
      exemptedPlanExpiresAt:
        businessBasic?.exemptedPlanExpiresAt ??
        selectedBusiness?.businessBasic?.exemptedPlanExpiresAt ??
        providerUser?.exemptedPlanExpiresAt,
    }),
    [
      businessBasic?.exemptedPlanExpiresAt,
      businessBasic?.exemptedPlanId,
      providerUser,
      selectedBusiness?.businessBasic?.exemptedPlanExpiresAt,
      selectedBusiness?.businessBasic?.exemptedPlanId,
    ]
  );
  const bookingSubscription = bookingBillingData?.getBillingSubscription;
  const bookingBillingStatus = String(bookingSubscription?.status || "").toLowerCase();
  const bookingTierCode = getProductPlanTierCode(
    bookingSubscription,
    plansData?.plans || []
  );
  const hasBookingBillingAccess = useMemo(() => {
    if (!ACTIVE_BOOKING_STATUSES.has(bookingBillingStatus)) return false;
    if (!bookingSubscription?.planId) return true;
    return (
      bookingTierCode === "STANDARD" ||
      bookingTierCode === "PREMIUM" ||
      bookingTierCode === "ENTERPRISE"
    );
  }, [bookingBillingStatus, bookingSubscription?.planId, bookingTierCode]);
  // Directory choices belong to the active product page, not to every product
  // the customer owns. This keeps /biz/business focused when an account has
  // subscriptions to both care and booking products.
  const {
    showCareDirectoryToggle: canShowCareDirectoryToggle,
    showBookingDirectoryToggle: canShowBookingDirectoryToggle,
  } = getDirectoryToggleVisibility({
    productId,
    hasCareAccess,
    isHealthcareCategorySelected,
  });
  const hasAnyDirectoryAccess =
    canShowCareDirectoryToggle || canShowBookingDirectoryToggle;
  const requiresSubcategory = useMemo(
    () =>
      getDisplayedBusinessSubcategories(
        categories as any,
        form.businessCategoryId,
        productId
      ).length > 0,
    [categories, form.businessCategoryId, productId]
  );

  const isAFHVerified = useMemo(() => {
    const cs = (selectedBusiness as any)?.careSettings || {};
    const verified = !!cs?.afhLicenseVerified;
    const status = String(cs?.licenseStatus || cs?.facilityStatusCode || "").toUpperCase();
    const statusOk = !status || status === "ACTIVE" || status === "OP";
    const expiresRaw = cs?.licenseExpiresOn;
    const expires = expiresRaw ? new Date(expiresRaw) : null;
    const notExpired =
      !expires || Number.isNaN(expires.getTime()) || expires >= new Date();
    return verified && statusOk && notExpired;
  }, [selectedBusiness]);

  const isExistingBusinessEdit = Number(businessBasic?.id || selectedBusiness?.businessBasic?.id || 0) > 0;
  const visibleBusinessId = String(
    businessBasic?.id || selectedBusiness?.businessBasic?.id || ""
  ).trim();
  const [businessIdCopied, setBusinessIdCopied] = useState(false);
  const copyBusinessId = async () => {
    if (!visibleBusinessId) return;
    try {
      await navigator.clipboard?.writeText(visibleBusinessId);
      setBusinessIdCopied(true);
      window.setTimeout(() => setBusinessIdCopied(false), 1800);
    } catch (_error) {
      setBusinessIdCopied(false);
    }
  };
  const manualVerificationRequired =
    manualVerificationRequested &&
    shouldInitNew &&
    !isExistingBusinessEdit;
  const forcePrivateListing =
    false;
  const newBusinessDraftKey = useMemo(
    () =>
      JSON.stringify({
        suggestedAfhLicense,
        suggestedBusinessName,
        suggestedCategoryId,
        suggestedSubcategoryId,
        manualVerificationRequired,
        pathname: location.pathname,
      }),
    [
      suggestedAfhLicense,
      suggestedBusinessName,
      suggestedCategoryId,
      suggestedSubcategoryId,
      manualVerificationRequired,
      location.pathname,
    ]
  );
  const getCurrentBusinessVisibilityKey = React.useCallback(
    (businessId?: number | string | null) => {
      const resolvedBusinessId = String(businessId || "").trim();
      if (resolvedBusinessId) return resolvedBusinessId;
      return `draft:${newBusinessDraftKey}`;
    },
    [newBusinessDraftKey]
  );

  const resetNewBusinessForm = React.useCallback(() => {
    setForm(
      buildFormWithDirectoryVisibility({
        baseForm: getInitialForm(),
        businessKey: getCurrentBusinessVisibilityKey(null),
        productId,
      })
    );
    setAddress(undefined);
    setErrors({});
    setTouched(false);
    setIsDirty(false);
    setAddrSubmitAttempted(false);
    setAddrExpanded(false);
    setSocialList(socialMediaOptions);
    setAnchorEl(null);
    setSubmitError("");
    setAfhLicenseNumber(suggestedAfhLicense);
    setAfhLicenseError("");
  }, [
    getCurrentBusinessVisibilityKey,
    productId,
    suggestedAfhLicense,
  ]);

  const resetExistingBusinessForm = React.useCallback(() => {
    if (!businessBasic) return;
    const businessId = Number(businessBasic.id || 0);
    setForm(
      buildFormWithDirectoryVisibility({
        baseForm: {
          ...businessBasic,
          ...resolveBusinessCategoryFields(selectedBusiness, businessBasic),
        } as BusinessBasic,
        businessKey: getCurrentBusinessVisibilityKey(businessId),
        productId,
      })
    );
    setAddress({
      street: businessBasic.street,
      countryId: businessBasic.countryId,
      stateId: businessBasic.stateId,
      cityId: businessBasic.cityId,
      zipcode: businessBasic.zipcode,
    });
    setErrors({});
    setTouched(false);
    setIsDirty(false);
    setAddrSubmitAttempted(false);
    setAddrExpanded(false);
    const used = (businessBasic.links || []).map((l) => l.platform);
    setSocialList(socialMediaOptions.filter((plat) => !used.includes(plat)));
    setAnchorEl(null);
    setSubmitError("");
    setAfhLicenseNumber((selectedBusiness as any)?.careSettings?.afhLicenseNumber || "");
    setAfhLicenseError("");
  }, [
    businessBasic,
    getCurrentBusinessVisibilityKey,
    productId,
    resolveBusinessCategoryFields,
    selectedBusiness,
  ]);

  const handleCancel = React.useCallback(() => {
    if (shouldInitNew) {
      resetNewBusinessForm();
      if (onCancel) {
        onCancel();
        return;
      }
      if (explicitNewBusiness) {
        clearNewBusinessRoute();
      }
      return;
    }

    resetExistingBusinessForm();
    onCancel?.();
  }, [
    clearNewBusinessRoute,
    explicitNewBusiness,
    onCancel,
    resetExistingBusinessForm,
    resetNewBusinessForm,
    shouldInitNew,
  ]);

  // --------------------------------------------------------------------
  // Combined New vs. Edit initializer
  // --------------------------------------------------------------------
  useEffect(() => {
    if (shouldInitNew) {
      // In explicit "new business" mode, reinitialize only when the incoming
      // create-draft seed changes, not on unrelated store churn.
      const shouldReuseCurrentDraft =
        hasInitNewFormRef.current &&
        initializedNewDraftKeyRef.current === newBusinessDraftKey;
      if (shouldReuseCurrentDraft) return;
      initializedBusinessIdRef.current = null;
      initializedNewDraftKeyRef.current = newBusinessDraftKey;
      setForm(
        buildFormWithDirectoryVisibility({
          baseForm: getInitialForm(),
          businessKey: getCurrentBusinessVisibilityKey(null),
          productId,
        })
      );
      setAddress(undefined);
      setErrors({});
      setTouched(false);
      setIsDirty(false);
      setSocialList(socialMediaOptions);
      setAnchorEl(null);
      setSubmitError("");
      setAfhLicenseNumber(suggestedAfhLicense);
      setAfhLicenseError("");
      hasInitNewFormRef.current = true;
      return;
    }

    hasInitNewFormRef.current = false;
    initializedNewDraftKeyRef.current = null;
    if (businessBasic) {
      const incomingBusinessId = Number(businessBasic.id || 0);
      const sameBusinessAsInitialized =
        initializedBusinessIdRef.current != null &&
        initializedBusinessIdRef.current === incomingBusinessId;

      // Avoid clobbering local unsaved edits when the same business refreshes in Redux/Apollo.
      if (isDirty && sameBusinessAsInitialized) {
        return;
      }

      setForm({
        ...buildFormWithDirectoryVisibility({
          baseForm: {
            ...businessBasic,
            ...resolveBusinessCategoryFields(selectedBusiness, businessBasic),
          } as BusinessBasic,
          businessKey: getCurrentBusinessVisibilityKey(incomingBusinessId),
          productId,
        }),
      });
      setAddress({
        street: businessBasic.street,
        countryId: businessBasic.countryId,
        stateId: businessBasic.stateId,
        cityId: businessBasic.cityId,
        zipcode: businessBasic.zipcode,
      });
      setErrors({});
      setTouched(false);
      setIsDirty(false);
      const used = (businessBasic.links || []).map((l) => l.platform);
      setSocialList(socialMediaOptions.filter((plat) => !used.includes(plat)));
      setAnchorEl(null);
      setAfhLicenseNumber((selectedBusiness as any)?.careSettings?.afhLicenseNumber || "");
      setAfhLicenseError("");
      initializedBusinessIdRef.current = incomingBusinessId || null;
    }
  }, [
    shouldInitNew,
    businessBasic,
    selectedBusiness,
    suggestedAfhLicense,
    suggestedBusinessName,
    suggestedCategoryId,
    suggestedSubcategoryId,
    manualVerificationRequired,
    newBusinessDraftKey,
    isDirty,
    getCurrentBusinessVisibilityKey,
    productId,
    resolveBusinessCategoryFields,
  ]);

  useEffect(() => {
    const healthcareCategory = getHealthcareTopLevelBusinessCategory(categories as any);
    if (!healthcareCategory?.id) return;

    const displayedTopLevelCategories = getDisplayedBusinessTopLevelCategories(
      categories as any,
      productId
    );
    const topLevelSelectionExists = displayedTopLevelCategories.some(
      (category: any) => Number(category.id) === Number(form.businessCategoryId || 0)
    );
    const currentEffectiveCategoryId = Number(
      (form as any).businessSubcategoryId || form.businessCategoryId || 0
    );
    if (!currentEffectiveCategoryId || topLevelSelectionExists) return;

    const currentEffectiveCategory = categories.find(
      (category: any) => Number(category.id) === currentEffectiveCategoryId
    );
    if (
      !currentEffectiveCategory ||
      !isHealthcareBusinessCategory(currentEffectiveCategory as any) ||
      isHealthcareCategoryName(currentEffectiveCategory?.name)
    ) {
      return;
    }

    setForm((prev: any) => ({
      ...prev,
      businessCategoryId: Number(healthcareCategory.id),
      businessSubcategoryId: currentEffectiveCategoryId,
    }));
  }, [
    categories,
    productId,
    form.businessCategoryId,
    (form as any).businessSubcategoryId,
  ]);

  useEffect(() => {
    if (!manualVerificationRequired) return;
    setForm((prev: any) => ({
      ...prev,
      displayOnCareDirectory: false,
      displayOnBookingDirectory: false,
      displayOnBookingPage: false,
    }));
  }, [manualVerificationRequired, setForm]);

  useEffect(() => {
    if (hasAnyDirectoryAccess) return;
    if (
      !form.displayOnBookingPage &&
      !(form as any).displayOnCareDirectory &&
      !(form as any).displayOnBookingDirectory
    ) {
      return;
    }
    setForm((prev: any) => ({
      ...prev,
      displayOnCareDirectory: false,
      displayOnBookingDirectory: false,
      displayOnBookingPage: false,
    }));
  }, [
    hasAnyDirectoryAccess,
    form.displayOnBookingPage,
    (form as any).displayOnCareDirectory,
    (form as any).displayOnBookingDirectory,
    setForm,
  ]);

  // Ensure selected business loads if only id is known on providerUser
  useEffect(() => {
    if (explicitNewBusiness) return;
    if (!selectedBusinessFromStore && providerUser?.defaultBusinessId) {
      const inStore = allBusinesses.find(
        (b: any) => Number(b?.businessBasic?.id) === Number(providerUser.defaultBusinessId)
      );
      if (inStore?.businessBasic?.id) {
        dispatch(setSelectedBusiness(Number(inStore.businessBasic.id)));
        return;
      }
      reloadBusinesses().then((result) => {
        const businesses = result?.data?.myBusinesses || [];
        const matched = businesses.find(
          (b: any) =>
            Number(b?.id ?? b?.businessBasic?.id) ===
            Number(providerUser.defaultBusinessId)
        );
        if (matched) {
          dispatch(setSelectedBusiness(Number(matched?.id ?? matched?.businessBasic?.id)));
        }
      });
    }
  }, [
    explicitNewBusiness,
    selectedBusinessFromStore,
    allBusinesses,
    providerUser.defaultBusinessId,
    reloadBusinesses,
    dispatch,
  ]);

  // --------------------------------------------------------------------
  // Permission logic — robust + optimistic editable on first paint
  // --------------------------------------------------------------------
  const businessStaffs = selectedBusiness?.businessStaffs || [];

  // Match as staff by id OR by userId (common schema)
  const myStaff = useMemo(() => {
    const sid = providerUser?.staffId;
    const uid = providerUser?.userId;
    return businessStaffs.find(
      (s: any) =>
        Number(s?.id) === Number(sid) || Number(s?.userId) === Number(uid)
    );
  }, [businessStaffs, providerUser?.staffId, providerUser?.userId]);

  // We "know" access once any of these are true
  const accessKnown = useMemo(() => {
    return (
      shouldInitNew ||
      Boolean(selectedBusiness) ||
      Boolean(businessBasic?.createdBy)
    );
  }, [shouldInitNew, selectedBusiness, businessBasic?.createdBy]);

  const canEdit = useMemo(() => {
    if (shouldInitNew) return true; // creating a new business
    if (!accessKnown) return true; // optimistic until facts load
    if (businessBasic?.createdBy === providerUser?.userId) return true;
    const perms = myStaff?.role?.permissions || [];
    return hasPermission(perms, Permissions.Business.EDIT);
  }, [
    shouldInitNew,
    accessKnown,
    businessBasic?.createdBy,
    providerUser?.userId,
    myStaff?.role?.permissions,
  ]);

  // Final flag consumed by children
  const isFormDisabled = useMemo(() => {
    if (shouldInitNew) return false; // new form is editable
    if (!accessKnown) return false; // editable while loading auth data
    if (Boolean(disabledProp)) return true; // explicit parent override
    return !canEdit;
  }, [shouldInitNew, accessKnown, disabledProp, canEdit]);

  // --------------------------------------------------------------------

  // --- Handlers (input, dynamic fields, etc.) ---
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setTouched(true);
    setIsDirty(true);
    if (name === "description") {
      if (value.length > 4000) {
        setErrors((prev) => ({
          ...prev,
          description: "Maximum 4000 characters allowed.",
        }));
      } else {
        setErrors((prev) => {
          const { description, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target as { name: string; value: any };
    setForm((f) => {
      const nextForm = { ...f, [name]: value };
      if (name === "businessCategoryId") {
        (nextForm as any).businessCategoryId = value ? Number(value) : null;
        const nextSubcategories = getDisplayedBusinessSubcategories(
          categories as any,
          value,
          productId
        );
        const currentSubcategoryId = Number((f as any).businessSubcategoryId || 0);
        const isCurrentSubcategoryValid = nextSubcategories.some(
          (category) => Number(category.id) === currentSubcategoryId
        );
        if (!isCurrentSubcategoryValid) {
          (nextForm as any).businessSubcategoryId = null;
        }
      } else if (name === "businessSubcategoryId") {
        (nextForm as any).businessSubcategoryId = value ? Number(value) : null;
      }
      return nextForm;
    });
    if (name === "businessCategoryId" || name === "businessSubcategoryId") {
      setErrors((prev) => {
        const { businessSubcategoryId, ...rest } = prev;
        return rest;
      });
    }
    setTouched(true);
    setIsDirty(true);
  };

  // Checkbox handler with plan check
  const handleCheckBoxStateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = event.target;

    const visibilityDirectory: DirectoryVisibilityKey | null =
      name === "displayOnCareDirectory"
        ? "care"
        : name === "displayOnBookingDirectory"
          ? "booking"
          : name === "displayOnBookingPage"
            ? "booking"
            : null;

    if (visibilityDirectory) {


      if (
        shouldRequireBusinessVisibilityUpgrade({
          productId: visibilityDirectory,
          checked,
          providerUser: visibilityAccessUser,
          isCareBusiness: isAFHSelected,
          hasBookingBillingAccess,
          minPlan: standardPlanRequired,
        })
      ) {
        setCheckPlanNeeded(true);
        return;
      }
    }

    setForm((prev: any) => {
      const next = {
        ...prev,
        [name]: checked,
      };

      if (visibilityDirectory === "care") {
        next.displayOnCareDirectory = checked;
        writeStoredDirectoryVisibility(
          getCurrentBusinessVisibilityKey(prev.id),
          "care",
          checked
        );
      }

      if (visibilityDirectory === "booking") {
        next.displayOnBookingDirectory = checked;
        writeStoredDirectoryVisibility(
          getCurrentBusinessVisibilityKey(prev.id),
          "booking",
          checked
        );
      }

      if (visibilityDirectory === productId) {
        next.displayOnBookingPage = checked;
      }

      return next;
    });
    setTouched(true);
    setIsDirty(true);
  };

  const handleDynamicFieldChange = (
    key: DynamicFieldKey,
    idx: number,
    value: any
  ) => {
    setForm((f) => {
      const arr = [...(f[key] || [])];
      arr[idx] = value;
      return { ...f, [key]: arr };
    });
    setTouched(true);
    setIsDirty(true);
  };

  const handleUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!hasValidBusinessSlugCharacters(value)) {
      setErrors((prev) => ({
        ...prev,
        url: "Only letters, numbers, hyphens (-), underscores (_), and dots (.)",
      }));
      return;
    }
    setErrors((prev) => {
      const { url, ...rest } = prev;
      return rest;
    });
    setForm((f) => ({ ...f, url: value }));
    setTouched(true);
    setIsDirty(true);
  };

  const handlePhoneNumberChange = (idx: number, digits: string) => {
    setForm((f) => {
      const arr = [...(f.phoneNumbers || [])];
      arr[idx] = { ...arr[idx], phoneNumber: digits };
      return { ...f, phoneNumbers: arr };
    });
    setTouched(true);
    setIsDirty(true);
  };

  const handleFaxNumberChange = (digits: string) => {
    setForm((f) => ({ ...f, faxNumber: digits || "" }));
    setIsDirty(true);
  };

  const handleAddDynamic = (key: "emails" | "phoneNumbers") => {
    if (key === "phoneNumbers") {
      setForm((f) => ({
        ...f,
        phoneNumbers: [
          ...(f.phoneNumbers || []),
          { countryCode: "", phoneNumber: "" },
        ],
      }));
    } else {
      setForm((f) => ({
        ...f,
        emails: [...(f.emails || []), ""],
      }));
    }
    setTouched(true);
    setIsDirty(true);
  };

  const handleDeleteDynamic = (
    key: "emails" | "phoneNumbers" | "links",
    idx: number
  ) => {
    setForm((f) => ({
      ...f,
      [key]: Array.isArray(f[key])
        ? [...(f[key] as any[]).filter((_, i) => i !== idx)]
        : [],
    }));
    setTouched(true);
    setIsDirty(true);
  };

  const handleAddLink = (platform: string) => {
    setForm((f) => ({
      ...f,
      links: [...(f.links || []), { platform, url: "" }],
    }));
    setSocialList((s) => s.filter((x) => x !== platform));
    setAnchorEl(null);
    setTouched(true);
    setIsDirty(true);
  };

  // --- Submit (create/update) ---
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const normalizedAfhLicense = normalizeLicense(afhLicenseNumber);
    setSubmitError("");


    setAfhLicenseError("");

    if (requiresSubcategory && !Number((form as any).businessSubcategoryId || 0)) {
      setErrors((prev) => ({
        ...prev,
        businessSubcategoryId: "Subcategory is required.",
      }));
      return;
    }
    setErrors((prev) => {
      const { businessSubcategoryId, ...rest } = prev;
      return rest;
    });

    // Show the header error state if address invalid
    if (!addressValid) {
      setAddrSubmitAttempted(true);
      setAddrExpanded(true);
      addrHeaderRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }
    const validCreatedBy =
      Number(businessBasic?.createdBy) > 0
        ? Number(businessBasic?.createdBy)
        : providerUser?.userId;
    try {
      setAddrSubmitAttempted(false);
      let updatedBasic: BusinessBasic = {
        createdBy: validCreatedBy,
        ...form,
        businessCategoryId:
          form.businessCategoryId != null ? Number(form.businessCategoryId) : null,
        businessSubcategoryId:
          (form as any).businessSubcategoryId != null
            ? Number((form as any).businessSubcategoryId)
            : null,
        ...address,
        claimStatus: "CLAIMED",
        stateId:
          typeof address?.stateId === "number" && address.stateId !== -1
            ? address.stateId
            : form.stateId,
        cityId:
          typeof address?.cityId === "number" && address.cityId !== -1
            ? address.cityId
            : form.cityId,
        countryId:
          typeof address?.countryId === "number" && address.countryId !== -1
            ? address.countryId
            : form.countryId,
        displayOnBookingPage:
          Boolean((form as any).displayOnBookingDirectory),
      };



      if (shouldInitNew || !businessBasic) {
        const {
          id,
          images,
          displayOnCareDirectory,
          displayOnBookingDirectory,
          ...createBusinessInput
        } = updatedBasic as any;

        const response = await postBusiness({
          variables: { input: createBusinessInput },
        });

        if (response.data?.createBusiness) {
          const newBiz = response.data.createBusiness;
          const normalizedCreatedBusiness =
            normalizeBusinessData(newBiz)?.[0] || null;
          const createdBusinessBasic =
            normalizedCreatedBusiness?.businessBasic || {
              ...updatedBasic,
              id: Number(newBiz?.id || 0),
            };
          const createdBusinessId = Number(
            createdBusinessBasic?.id || newBiz?.id || 0
          );

          setSubmitError("");
          setNewBizId(createdBusinessId);
          initializedBusinessIdRef.current = createdBusinessId || null;

          try {
            await reloadBusinesses();
          } catch (reloadError) {
            console.error(
              "Business created, but reloading businesses failed:",
              reloadError
            );
          }

          if (createdBusinessId > 0) {
            setStoredVertical("booking");
            initializeWorkspaceProductsForNewBusiness(
              createdBusinessId,
              productId
            );
            try {
              await updateDefaultBusiness({
                variables: { defaultBusinessId: createdBusinessId },
              });
            } catch (defaultBusinessError) {
              console.error(
                "Business created, but updating default business failed:",
                defaultBusinessError
              );
            }
            await syncBusinessContext(createdBusinessId);
          }

          if (explicitNewBusiness) {
            clearNewBusinessRoute();
          }

          // Fire-and-forget email (non-blocking UX)
          try {
            await sendEmail({
              variables: {
                to: providerUser.email,
                subject: `Congratulations! Your business "${response.data?.createBusiness?.name}" is now live on Timely365`,
                text: `Hello,\n\nCongratulations! You have successfully created your business "${response.data?.createBusiness?.name}" on Timely365.\nYou can now access your business dashboard and manage your operations.\n\nThank you for choosing Timely365.\n\nBest regards,\nThe Timely365 Team`,
              },
            });
          } catch (err) {
            console.error("Error sending email during business creation:", err);
          }

          setForm(
            buildFormWithDirectoryVisibility({
              baseForm: {
                ...createdBusinessBasic,
                ...resolveBusinessCategoryFields(
                  newBiz,
                  createdBusinessBasic,
                  suggestedCategoryId,
                  suggestedSubcategoryId
                ),
              } as BusinessBasic,
              businessKey: getCurrentBusinessVisibilityKey(createdBusinessId),
              productId,
            })
          );
          setAddress({
            street: createdBusinessBasic.street,
            countryId: createdBusinessBasic.countryId,
            stateId: createdBusinessBasic.stateId,
            cityId: createdBusinessBasic.cityId,
            zipcode: createdBusinessBasic.zipcode,
          });
          setShowConfirmation(true);
          setIsDirty(false);
          onSubmitSuccess?.();
          hasInitNewFormRef.current = false;
          setAfhLicenseNumber(
            String(
              (normalizedCreatedBusiness as any)?.careSettings?.afhLicenseNumber ||
                (newBiz as any)?.careSettings?.afhLicenseNumber ||
                ("")
            )
          );
          return;
        }
      } else {
        const {
          images,
          displayOnCareDirectory,
          displayOnBookingDirectory,
          ...updateBusinessInput
        } = updatedBasic as any;

        const response = await updateBusinessM({
          variables: {
            input: {
              ...updateBusinessInput,
              createdBy: validCreatedBy,
            },
          },
        });
        const savedBusiness = response?.data?.updateBusiness;
        const normalizedSavedBusiness = savedBusiness
          ? normalizeBusinessData(savedBusiness)?.[0] || null
          : null;
        const savedBusinessBasic =
          normalizedSavedBusiness?.businessBasic || updatedBasic;
        const savedBusinessId = Number(
          savedBusinessBasic.id || updatedBasic.id || businessBasic?.id || 0
        );
        setForm(
          buildFormWithDirectoryVisibility({
            baseForm: {
              ...savedBusinessBasic,
              ...resolveBusinessCategoryFields(savedBusiness, savedBusinessBasic),
            } as BusinessBasic,
            businessKey: getCurrentBusinessVisibilityKey(
              savedBusinessId
            ),
            productId,
          })
        );
        setAddress({
          street: savedBusinessBasic.street,
          countryId: savedBusinessBasic.countryId,
          stateId: savedBusinessBasic.stateId,
          cityId: savedBusinessBasic.cityId,
          zipcode: savedBusinessBasic.zipcode,
        });
        await reloadBusinesses();
        if (
          savedBusinessId > 0 &&
          savedBusinessId === Number(providerUser?.defaultBusinessId || 0)
        ) {
          setStoredVertical("booking");
          await syncBusinessContext(savedBusinessId);
        } else if (savedBusinessId > 0) {
          dispatch(setSelectedBusiness(savedBusinessId));
        }
        setIsDirty(false);
        onSubmitSuccess?.();
      }
    } catch (error) {
      const graphQLErrorMessage =
        (error as any)?.graphQLErrors?.[0]?.message ||
        (error as any)?.networkError?.result?.errors?.[0]?.message ||
        (error as any)?.message ||
        "Unable to create/update business.";

      if (/AFH license number is required/i.test(graphQLErrorMessage)) {
        setAfhLicenseError("AFH license number is required.");
      }
      setSubmitError(graphQLErrorMessage);
      console.error("Unable to create/update business:", error);
    }
  };

  const selectedCountry = countries.find(
    (c: any) => Number(c.id) === Number(form.countryId)
  );

  const computedPhoneCode = useMemo(() => {
    if (!selectedCountry?.phone_code) return "";
    const code = selectedCountry.phone_code.toString();
    return code.startsWith("0") || code.startsWith("+") ? code : `+${code}`;
  }, [selectedCountry]);

  if (loadingCountries || loadingSupportedCountries || loadingCats) return <p>Loading...</p>;
  if (supportedCountriesError) return <p>Could not load supported countries. Refresh to try again.</p>;

  const contactForm = {
    emails: form.emails ?? [""],
    phoneNumbers: form.phoneNumbers ?? [
      { countryCode: computedPhoneCode || "", phoneNumber: "" },
    ],
    faxNumber: form.faxNumber ?? "",
    links: form.links ?? [],
  };

  return (
    <>
      <FormProvider {...methods}>
        <ConfirmModal open={showConfirmation} businessId={newBizId} />
        <FormControl component="form" onSubmit={handleSubmit} fullWidth>
          {!!submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          {checkPlanNeeded && (
            <UpgradePlan
              featureName={
                "Standalone Booking subscription"
              }
              requiredPlanId={standardPlanRequired}
              applyExemption={productId !== "booking"}
              pricingProduct={"booking"}
              onClose={() => setCheckPlanNeeded(false)}
            />
          )}

          <Box
            sx={{
              width: "100%",
              mx: "auto",
              minWidth: 0,
            }}
          >
          {isExistingBusinessEdit && visibleBusinessId ? (
            <Box
              sx={{
                mb: 2,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Business ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                  {visibleBusinessId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use this ID to connect your workspace to an external integration.
                </Typography>
              </Box>
              <Button
                type="button"
                variant="outlined"
                onClick={copyBusinessId}
                sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
              >
                {businessIdCopied ? "Copied" : "Copy ID"}
              </Button>
            </Box>
          ) : null}
          <BusinessSettingsPage
            productId={productId}
            baseFormProps={{
              form,
              setForm,
              errors,
              afhLicenseNumber,
              setAfhLicenseNumber: (value: string) => {
                setAfhLicenseNumber(value);
                setIsDirty(true);
              },
              afhLicenseError,
              clearAfhLicenseError: () => {
                if (afhLicenseError) setAfhLicenseError("");
              },
              t,
              isFormDisabled,
              rootClientPath,
              selectedBusiness,
              isDirty,
              categories,
              handleInputChange,
              handleCheckBoxStateChange,
              handleUrlChange,
              tooltipData,
              handleSelectChange,
              forcePrivateListing,
              address,
              setAddress,
              addressValid,
              addrExpanded,
              setAddrExpanded,
              addrSubmitAttempted,
              addrHeaderRef,
              countries,
              setErrors,
              setTouched,
              setIsDirty,
              contactForm,
              anchorEl,
              setAnchorEl,
              socialList,
              handleAddDynamic,
              handleDeleteDynamic,
              handleDynamicFieldChange,
              handleAddLink,
              handlePhoneNumberChange,
              handleFaxNumberChange,
              computedPhoneCode,
            }}
            careFormProps={{
              isFormDisabled,
              handleSelectChange,
              displayOnCareDirectory: !!(form as any).displayOnCareDirectory,
              displayOnBookingDirectory: !!(form as any).displayOnBookingDirectory,
              handleCheckBoxStateChange,
              forcePrivateListing,
              showCareDirectoryToggle: canShowCareDirectoryToggle,
              showBookingDirectoryToggle: canShowBookingDirectoryToggle,
            }}
            bookingFormProps={{
              isFormDisabled,
              categories,
              businessCategoryId: form.businessCategoryId,
              categoryError: errors.businessCategoryId,
              handleSelectChange,
              displayOnCareDirectory: !!(form as any).displayOnCareDirectory,
              displayOnBookingDirectory: !!(form as any).displayOnBookingDirectory,
              handleCheckBoxStateChange,
              forcePrivateListing,
              showCareDirectoryToggle: canShowCareDirectoryToggle,
              showBookingDirectoryToggle: canShowBookingDirectoryToggle,
            }}
          />


            {/* Actions */}
            <Grid item xs={12} marginTop={4}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  disabled={isFormDisabled || (!isDirty && !shouldInitNew && !onCancel)}
                  onClick={handleCancel}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isFormDisabled || !isDirty}
                >
                  {t("Save")}
                </Button>
              </Box>
            </Grid>
          </Box>
        </FormControl>
      </FormProvider>
    </>
  );
};

export default BusinessForm;
